import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon, ClockIcon, CalendarIcon, ZapIcon, TargetIcon, FlameIcon, ShieldCheckIcon, DownloadIcon, ArrowRightIcon, SparklesIcon, HeartIcon, BookOpenIcon, ListChecksIcon, UtensilsIcon, ActivityIcon, XIcon } from 'lucide-react';
/* ─── Animation Wrapper ─── */
function FadeInSection({
  children,
  className = '',
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 40
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.7,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className={className} data-id="element-1">
      {children}
    </motion.div>;
}
/* ─── Hero Lifestyle Image ─── */
function HeroLifestyleImage() {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-40px'
  }} transition={{
    duration: 0.8,
    ease: [0.22, 1, 0.36, 1]
  }} className="w-full max-w-4xl mx-auto px-6 py-10" data-id="element-2">
      <img src="https://cdn.magicpatterns.com/uploads/dFa6fPgxND2UhAYDHuJR6G/heylloyd-image-1771195926079.png" alt="Mujer relajada en casa con esterilla de yoga y libreta" className="w-full rounded-3xl shadow-lg object-cover" data-id="element-3" />
    </motion.div>;
}
function StaggerItem({
  children,
  index
}: {
  children: React.ReactNode;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-40px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.1,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-4">
      {children}
    </motion.div>;
}
/* ─── CTA Button ─── */
function CTAButton({
  large = false
}: {
  large?: boolean;
}) {
  return <motion.a href="#comprar" whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.98
  }} className={`cta-glow inline-flex items-center justify-center gap-3 bg-[#FF6B5A] hover:bg-[#E85A4A] text-white font-semibold rounded-full transition-colors ${large ? 'px-10 py-5 text-lg md:text-xl' : 'px-8 py-4 text-base md:text-lg'}`} data-id="element-5">
      Quiero empezar hoy el Sistema 21×20
      <ArrowRightIcon className="w-5 h-5" data-id="element-6" />
    </motion.a>;
}
/* ─── Divider ─── */
function SectionDivider({
  dark = false
}: {
  dark?: boolean;
}) {
  return <div className="flex justify-center py-2" data-id="element-7">
      <div className={`w-16 h-[2px] rounded-full ${dark ? 'bg-white/10' : 'bg-[#1A1A1A]/10'}`} data-id="element-8" />
    </div>;
}
/* ─── Legal Modal ─── */
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-9">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} data-id="element-10" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-white rounded-3xl shadow-2xl z-50 flex flex-col overflow-hidden" data-id="element-11">
            <div className="flex items-center justify-between px-8 py-6 border-b border-[#1A1A1A]/10" data-id="element-12">
              <h2 className="font-serif-display text-2xl text-[#1A1A1A]" data-id="element-13">
                {title}
              </h2>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-[#1A1A1A]/5 flex items-center justify-center hover:bg-[#1A1A1A]/10 transition-colors" data-id="element-14">
                <XIcon className="w-5 h-5 text-[#1A1A1A]/60" data-id="element-15" />
              </button>
            </div>
            <div className="px-8 py-6 overflow-y-auto text-[#1A1A1A]/70 text-base leading-relaxed space-y-4" data-id="element-16">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
/* ═══════════════════════════════════════════════════════════════
   SALES PAGE
   ═══════════════════════════════════════════════════════════════ */
export function SalesPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // ─── Meta Pixel ───
  useEffect(() => {
    if (typeof window !== 'undefined' && !(window as any).fbq) {
      const f = window as any;
      const n = f.fbq = function (...args: any[]) {
        if (n.callMethod) {
          n.callMethod.apply(n, args);
        } else {
          n.queue.push(args);
        }
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [] as any[];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
      f.fbq('init', '782547894469810');
      f.fbq('track', 'PageView');
    }
  }, []);
  const handleAddToCart = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart');
    }
  };
  return <main className="w-full overflow-x-hidden" data-id="element-17">
      {/* ─── 1) HERO ─── */}
      <section className="relative bg-[#1A1A1A] text-white min-h-screen flex items-center" data-id="element-18">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A] via-[#1A1A1A] to-[#2A2A2A]" data-id="element-19" />
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 py-20 md:py-28" data-id="element-20">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center" data-id="element-21">
            <div data-id="element-22">
              <FadeInSection data-id="element-23">
                <p className="text-[#FF6B5A] font-semibold text-sm tracking-widest uppercase mb-6" data-id="element-24">
                  Reto 21 días · Principiantes
                </p>
              </FadeInSection>
              <FadeInSection delay={0.1} data-id="element-25">
                <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-6" data-id="element-26">
                  Ponte en forma en casa en 21 días{' '}
                  <span className="text-[#FF6B5A] italic" data-id="element-27">
                    (aunque lleves meses diciendo "el lunes empiezo")
                  </span>
                </h1>
              </FadeInSection>
              <FadeInSection delay={0.2} data-id="element-28">
                <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-10 max-w-lg" data-id="element-29">
                  Con el Sistema 21×20: entrenos de 20 minutos, progresión en 3
                  fases y un plan diario para que no dependas de motivación ni
                  de gimnasio.
                </p>
              </FadeInSection>
              <FadeInSection delay={0.3} data-id="element-30">
                <CTAButton large data-id="element-31" />
              </FadeInSection>
              <FadeInSection delay={0.4} data-id="element-32">
                <div className="flex items-center gap-6 mt-8 text-sm text-white/50" data-id="element-33">
                  <span className="flex items-center gap-2" data-id="element-34">
                    <DownloadIcon className="w-4 h-4" data-id="element-35" /> Descarga inmediata
                  </span>
                  <span className="flex items-center gap-2" data-id="element-36">
                    <ShieldCheckIcon className="w-4 h-4" data-id="element-37" /> Garantía 7 días
                  </span>
                </div>
              </FadeInSection>
            </div>
            <FadeInSection delay={0.2} data-id="element-38">
              <div className="flex justify-center md:justify-end" data-id="element-39">
                <motion.img src="https://cdn.magicpatterns.com/uploads/8wiWiiRTPFWktZxe72h4VN/mockup-retosingym.png" alt="Mockup del ebook Reto 21 días: Cuerpo en Casa" className="w-72 md:w-96 drop-shadow-2xl" initial={{
                opacity: 0,
                scale: 0.9,
                rotate: -2
              }} whileInView={{
                opacity: 1,
                scale: 1,
                rotate: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-40" />
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ─── 2) APERTURA (empatía + dolor) ─── */}
      <section className="bg-[#FAF7F4] text-[#1A1A1A]" data-id="element-41">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28" data-id="element-42">
          <FadeInSection data-id="element-43">
            <p className="text-xl md:text-2xl leading-relaxed mb-8" data-id="element-44">
              Si ahora mismo sientes que estás <strong data-id="element-45">"fuera de forma"</strong>
              , quiero que te relajes un segundo.
            </p>
          </FadeInSection>
          <FadeInSection delay={0.1} data-id="element-46">
            <p className="text-xl md:text-2xl leading-relaxed mb-4" data-id="element-47">
              Porque la mayoría no está fuera de forma.
            </p>
          </FadeInSection>
          <FadeInSection delay={0.15} data-id="element-48">
            <p className="font-serif-display text-3xl md:text-4xl text-[#FF6B5A] mb-10" data-id="element-49">
              Está fuera de rutina.
            </p>
          </FadeInSection>
          <FadeInSection delay={0.2} data-id="element-50">
            <p className="text-lg md:text-xl text-[#1A1A1A]/70 leading-relaxed mb-6" data-id="element-51">
              Y eso se nota en todo: te cansas antes, te ves más "blanda", la
              ropa aprieta donde antes no… y lo peor es esa sensación de:
            </p>
          </FadeInSection>
          <FadeInSection delay={0.25} data-id="element-52">
            <blockquote className="border-l-4 border-[#FF6B5A] pl-6 py-2 mb-10" data-id="element-53">
              <p className="font-serif-display text-2xl md:text-3xl italic text-[#1A1A1A]/80" data-id="element-54">
                "Sé lo que tengo que hacer… pero no lo estoy haciendo."
              </p>
            </blockquote>
          </FadeInSection>
          <FadeInSection delay={0.3} data-id="element-55">
            <div className="text-lg md:text-xl text-[#1A1A1A]/70 leading-relaxed space-y-4" data-id="element-56">
              <p data-id="element-57">Has probado a empezar fuerte.</p>
              <p data-id="element-58">Has probado a seguir rutinas de Instagram.</p>
              <p data-id="element-59">Incluso has pensado en apuntarte al gimnasio.</p>
              <p data-id="element-60">Y aun así… acabas volviendo al mismo punto.</p>
              <p className="pt-4" data-id="element-61">No porque no puedas.</p>
              <p data-id="element-62">
                <strong className="text-[#1A1A1A]" data-id="element-63">
                  Sino porque el sistema que has intentado usar no encaja con tu
                  vida.
                </strong>
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 3) AGITACIÓN ─── */}
      <section className="bg-[#1A1A1A] text-white" data-id="element-64">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28" data-id="element-65">
          <FadeInSection data-id="element-66">
            <h2 className="font-serif-display text-3xl md:text-4xl mb-4" data-id="element-67">
              Lo frustrante no es solo el cuerpo.
            </h2>
            <p className="font-serif-display text-3xl md:text-4xl text-[#FF6B5A] mb-12" data-id="element-68">
              Es la película mental:
            </p>
          </FadeInSection>

          <div className="space-y-4 mb-12" data-id="element-69">
            <StaggerItem index={0} data-id="element-70">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-5" data-id="element-71">
                <p className="text-lg md:text-xl text-white/80 italic" data-id="element-72">
                  "No tengo tiempo."
                </p>
              </div>
            </StaggerItem>
            <StaggerItem index={1} data-id="element-73">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-5" data-id="element-74">
                <p className="text-lg md:text-xl text-white/80 italic" data-id="element-75">
                  "Estoy cansada."
                </p>
              </div>
            </StaggerItem>
            <StaggerItem index={2} data-id="element-76">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-5" data-id="element-77">
                <p className="text-lg md:text-xl text-white/80 italic" data-id="element-78">
                  "Si no lo hago perfecto, no cuenta."
                </p>
              </div>
            </StaggerItem>
            <StaggerItem index={3} data-id="element-79">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-5" data-id="element-80">
                <p className="text-lg md:text-xl text-white/80 italic" data-id="element-81">
                  "Empiezo el lunes."
                </p>
              </div>
            </StaggerItem>
            <StaggerItem index={4} data-id="element-82">
              <div className="flex items-start gap-4 bg-white/5 rounded-2xl px-6 py-5 border border-white/5" data-id="element-83">
                <XCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-84" />
                <p className="text-lg text-white/70" data-id="element-85">
                  No tienen un camino claro (¿hoy qué hago? ¿mañana qué toca?)
                </p>
              </div>
            </StaggerItem>
          </div>

          <FadeInSection delay={0.3} data-id="element-86">
            <p className="text-lg md:text-xl text-white/60 leading-relaxed mb-6" data-id="element-87">
              Y cuando pasan los días, aparece esa mezcla rara de culpa y
              resignación.
            </p>
            <p className="text-lg md:text-xl text-white/60 leading-relaxed mb-10" data-id="element-88">
              Como si cuidarte fuese un lujo.
            </p>
          </FadeInSection>
          <FadeInSection delay={0.4} data-id="element-89">
            <p className="text-lg md:text-xl text-white/60 leading-relaxed mb-2" data-id="element-90">
              Pero aquí viene la parte importante:
            </p>
            <p className="font-serif-display text-2xl md:text-3xl text-white" data-id="element-91">
              No te falta fuerza de voluntad.{' '}
              <span className="text-[#FF6B5A]" data-id="element-92">Te sobra fricción.</span>
            </p>
            <p className="text-lg md:text-xl text-white/60 leading-relaxed mt-4" data-id="element-93">
              Demasiadas decisiones, demasiada intensidad, demasiado "todo o
              nada".
            </p>
          </FadeInSection>

          <HeroLifestyleImage data-id="element-94" />
        </div>
      </section>

      {/* ─── 4) EL GIRO (Big Idea) ─── */}
      <section className="bg-[#FF6B5A] text-white" data-id="element-95">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28 text-center" data-id="element-96">
          <FadeInSection data-id="element-97">
            <p className="text-lg md:text-xl font-semibold mb-4 text-white/80" data-id="element-98">
              La solución no es entrenar más.
            </p>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-tight mb-8" data-id="element-99">
              Es entrenar de una forma que sea imposible de abandonar.
            </h2>
          </FadeInSection>
          <FadeInSection delay={0.15} data-id="element-100">
            <p className="text-lg md:text-xl leading-relaxed mb-4 text-white/90" data-id="element-101">
              Por eso creé el{' '}
              <strong data-id="element-102">Reto 21 días: Cuerpo en Casa (Principiantes).</strong>
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-white/80" data-id="element-103">
              Un plan pensado para mujeres con vida real: trabajo, cansancio,
              poco tiempo y cero ganas de complicarse.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 5) MECANISMO 21×20 ─── */}
      <section className="bg-[#FAF7F4] text-[#1A1A1A]" data-id="element-104">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28" data-id="element-105">
          <FadeInSection data-id="element-106">
            <p className="text-[#FF6B5A] font-semibold text-sm tracking-widest uppercase mb-4 text-center" data-id="element-107">
              El Mecanismo
            </p>
            <h2 className="font-serif-display text-3xl md:text-5xl text-center mb-16" data-id="element-108">
              Te presento el Sistema 21×20
            </h2>
          </FadeInSection>

          <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-109">
            <StaggerItem index={0} data-id="element-110">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#1A1A1A]/5" data-id="element-111">
                <div className="w-14 h-14 rounded-2xl bg-[#FF6B5A]/10 flex items-center justify-center mb-5" data-id="element-112">
                  <CalendarIcon className="w-7 h-7 text-[#FF6B5A]" data-id="element-113" />
                </div>
                <h3 className="font-serif-display text-2xl mb-3" data-id="element-114">21 días</h3>
                <p className="text-[#1A1A1A]/60 text-lg leading-relaxed" data-id="element-115">
                  Suficientes para crear inercia, sin sentir que te metes en una
                  cárcel de "vida fitness".
                </p>
              </div>
            </StaggerItem>

            <StaggerItem index={1} data-id="element-116">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#1A1A1A]/5" data-id="element-117">
                <div className="w-14 h-14 rounded-2xl bg-[#FF6B5A]/10 flex items-center justify-center mb-5" data-id="element-118">
                  <ClockIcon className="w-7 h-7 text-[#FF6B5A]" data-id="element-119" />
                </div>
                <h3 className="font-serif-display text-2xl mb-3" data-id="element-120">20 minutos</h3>
                <p className="text-[#1A1A1A]/60 text-lg leading-relaxed" data-id="element-121">
                  Lo bastante corto para que no negocies contigo. Puedes hacerlo
                  incluso en un día caótico.
                </p>
              </div>
            </StaggerItem>
          </div>

          {/* 3 Fases */}
          <FadeInSection data-id="element-122">
            <h3 className="font-serif-display text-2xl md:text-3xl text-center mb-10" data-id="element-123">
              3 fases de progresión
            </h3>
            <p className="text-center text-[#1A1A1A]/60 text-lg mb-10 max-w-xl mx-auto" data-id="element-124">
              Porque lo que rompe a la gente es empezar demasiado fuerte. Aquí
              progresas así:
            </p>
          </FadeInSection>

          <div className="space-y-5" data-id="element-125">
            <StaggerItem index={0} data-id="element-126">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#1A1A1A]/5 flex gap-6 items-start" data-id="element-127">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-128">
                  <TargetIcon className="w-6 h-6 text-emerald-600" data-id="element-129" />
                </div>
                <div data-id="element-130">
                  <h4 className="font-semibold text-lg mb-1" data-id="element-131">
                    <span className="text-[#FF6B5A]" data-id="element-132">Fase 1: BASE</span>{' '}
                    <span className="text-[#1A1A1A]/40 font-normal" data-id="element-133">
                      (Días 1–7)
                    </span>
                  </h4>
                  <p className="text-[#1A1A1A]/60 text-lg" data-id="element-134">
                    Activar y aprender sin agujetas bestias.
                  </p>
                </div>
              </div>
            </StaggerItem>

            <StaggerItem index={1} data-id="element-135">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#1A1A1A]/5 flex gap-6 items-start" data-id="element-136">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0" data-id="element-137">
                  <FlameIcon className="w-6 h-6 text-amber-600" data-id="element-138" />
                </div>
                <div data-id="element-139">
                  <h4 className="font-semibold text-lg mb-1" data-id="element-140">
                    <span className="text-[#FF6B5A]" data-id="element-141">Fase 2: FIRMEZA</span>{' '}
                    <span className="text-[#1A1A1A]/40 font-normal" data-id="element-142">
                      (Días 8–14)
                    </span>
                  </h4>
                  <p className="text-[#1A1A1A]/60 text-lg" data-id="element-143">
                    Más volumen, más "tono", sin reventarte.
                  </p>
                </div>
              </div>
            </StaggerItem>

            <StaggerItem index={2} data-id="element-144">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#1A1A1A]/5 flex gap-6 items-start" data-id="element-145">
                <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0" data-id="element-146">
                  <ZapIcon className="w-6 h-6 text-rose-600" data-id="element-147" />
                </div>
                <div data-id="element-148">
                  <h4 className="font-semibold text-lg mb-1" data-id="element-149">
                    <span className="text-[#FF6B5A]" data-id="element-150">Fase 3: EMPUJE</span>{' '}
                    <span className="text-[#1A1A1A]/40 font-normal" data-id="element-151">
                      (Días 15–21)
                    </span>
                  </h4>
                  <p className="text-[#1A1A1A]/60 text-lg" data-id="element-152">
                    Más intensidad con tempo y menos descanso (sin locuras).
                  </p>
                </div>
              </div>
            </StaggerItem>
          </div>

          <FadeInSection delay={0.1} data-id="element-153">
            <div className="text-center mt-10" data-id="element-154">
              <CTAButton large data-id="element-155" />
            </div>
          </FadeInSection>

          <FadeInSection delay={0.2} data-id="element-156">
            <div className="mt-14 bg-[#1A1A1A] rounded-3xl p-8 md:p-10 text-center" data-id="element-157">
              <h4 className="font-serif-display text-2xl text-white mb-3" data-id="element-158">
                Cero fricción
              </h4>
              <p className="text-white/60 text-lg max-w-md mx-auto" data-id="element-159">
                No tienes que decidir nada. Abres el PDF, miras el día que toca
                y lo haces.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 6) POR QUÉ NO FUNCIONÓ ANTES ─── */}
      <section className="bg-[#1A1A1A] text-white" data-id="element-160">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28" data-id="element-161">
          <FadeInSection data-id="element-162">
            <h2 className="font-serif-display text-3xl md:text-4xl mb-4" data-id="element-163">
              Por qué lo que has probado antes no te funcionó
            </h2>
            <p className="font-serif-display text-2xl md:text-3xl text-[#FF6B5A] mb-12" data-id="element-164">
              (y no fue culpa tuya)
            </p>
          </FadeInSection>

          <FadeInSection delay={0.1} data-id="element-165">
            <p className="text-lg text-white/60 mb-8" data-id="element-166">
              La mayoría de planes fallan porque:
            </p>
          </FadeInSection>

          <div className="space-y-4 mb-14" data-id="element-167">
            <StaggerItem index={0} data-id="element-168">
              <div className="flex items-start gap-4 bg-white/5 rounded-2xl px-6 py-5 border border-white/5" data-id="element-169">
                <XCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-170" />
                <p className="text-lg text-white/70" data-id="element-171">
                  Son demasiado largos (60–90 min) → se vuelven imposibles
                </p>
              </div>
            </StaggerItem>
            <StaggerItem index={1} data-id="element-172">
              <div className="flex items-start gap-4 bg-white/5 rounded-2xl px-6 py-5 border border-white/5" data-id="element-173">
                <XCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-174" />
                <p className="text-lg text-white/70" data-id="element-175">
                  Empiezan demasiado fuerte → agujetas/lesión/abandono
                </p>
              </div>
            </StaggerItem>
            <StaggerItem index={2} data-id="element-176">
              <div className="flex items-start gap-4 bg-white/5 rounded-2xl px-6 py-5 border border-white/5" data-id="element-177">
                <XCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-178" />
                <p className="text-lg text-white/70" data-id="element-179">
                  No están diseñados para principiantes reales
                </p>
              </div>
            </StaggerItem>
            <StaggerItem index={3} data-id="element-180">
              <div className="flex items-start gap-4 bg-white/5 rounded-2xl px-6 py-5 border border-white/5" data-id="element-181">
                <XCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-182" />
                <p className="text-lg text-white/70" data-id="element-183">
                  Dependen de motivación y "ganas"
                </p>
              </div>
            </StaggerItem>
            <StaggerItem index={4} data-id="element-184">
              <div className="flex items-start gap-4 bg-white/5 rounded-2xl px-6 py-5 border border-white/5" data-id="element-185">
                <XCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-186" />
                <p className="text-lg text-white/70" data-id="element-187">
                  No tienen un camino claro (¿hoy qué hago? ¿mañana qué toca?)
                </p>
              </div>
            </StaggerItem>
          </div>

          <FadeInSection delay={0.2} data-id="element-188">
            <motion.img src="https://cdn.magicpatterns.com/uploads/85izy6yJFSQfNP4WYEHBed/heylloyd-image-1771196274449.png" alt="Mujer frustrada sentada en el suelo con material de entrenamiento" className="w-full rounded-3xl shadow-lg object-cover mt-14 mb-14" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-189" />
          </FadeInSection>

          <FadeInSection delay={0.3} data-id="element-190">
            <div className="bg-[#FF6B5A]/10 border border-[#FF6B5A]/20 rounded-3xl p-8 md:p-10" data-id="element-191">
              <p className="text-lg text-white/60 mb-3" data-id="element-192">
                Este reto está construido justo al revés:
              </p>
              <p className="font-serif-display text-2xl md:text-3xl text-white" data-id="element-193">
                mínimo viable + estructura + progresión + continuidad.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 7) QUÉ ES ─── */}
      <section className="bg-[#FAF7F4] text-[#1A1A1A]" data-id="element-194">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28" data-id="element-195">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-196">
            <FadeInSection data-id="element-197">
              <div data-id="element-198">
                <p className="text-[#FF6B5A] font-semibold text-sm tracking-widest uppercase mb-4" data-id="element-199">
                  El Producto
                </p>
                <h2 className="font-serif-display text-3xl md:text-4xl mb-6" data-id="element-200">
                  Reto 21 días: Cuerpo en Casa (Principiantes)
                </h2>
                <p className="text-lg md:text-xl text-[#1A1A1A]/60 leading-relaxed mb-6" data-id="element-201">
                  Es un ebook PDF listo para usar, con:
                </p>
                <ul className="space-y-4" data-id="element-202">
                  <li className="flex items-start gap-3" data-id="element-203">
                    <CheckCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-204" />
                    <span className="text-lg text-[#1A1A1A]/70" data-id="element-205">
                      Un plan día a día (D1 a D21)
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-206">
                    <CheckCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-207" />
                    <span className="text-lg text-[#1A1A1A]/70" data-id="element-208">
                      Rutinas de 20 minutos con rondas, reps y descansos
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-209">
                    <CheckCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-210" />
                    <span className="text-lg text-[#1A1A1A]/70" data-id="element-211">
                      Variantes para hacerlo más fácil o más intenso sin cambiar
                      de plan
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-212">
                    <CheckCircleIcon className="w-6 h-6 text-[#FF6B5A] flex-shrink-0 mt-0.5" data-id="element-213" />
                    <span className="text-lg text-[#1A1A1A]/70" data-id="element-214">
                      Días suaves incluidos (para que no rompas la cadena)
                    </span>
                  </li>
                </ul>
                <div className="flex flex-wrap gap-3 mt-8 text-sm font-medium text-[#1A1A1A]/50" data-id="element-215">
                  <span className="bg-[#1A1A1A]/5 px-4 py-2 rounded-full" data-id="element-216">
                    Sin gimnasio
                  </span>
                  <span className="bg-[#1A1A1A]/5 px-4 py-2 rounded-full" data-id="element-217">
                    Sin material obligatorio
                  </span>
                  <span className="bg-[#1A1A1A]/5 px-4 py-2 rounded-full" data-id="element-218">
                    Sin necesidad de "saber entrenar"
                  </span>
                </div>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.2} data-id="element-219">
              <div className="flex justify-center" data-id="element-220">
                <img src="https://cdn.magicpatterns.com/uploads/8wiWiiRTPFWktZxe72h4VN/mockup-retosingym.png" alt="Ebook Reto 21 días" className="w-64 md:w-80 drop-shadow-xl" data-id="element-221" />
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ─── 8) QUÉ INCLUYE ─── */}
      <section className="bg-[#1A1A1A] text-white" data-id="element-222">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28" data-id="element-223">
          <FadeInSection data-id="element-224">
            <p className="text-[#FF6B5A] font-semibold text-sm tracking-widest uppercase mb-4 text-center" data-id="element-225">
              Todo lo que recibes
            </p>
            <h2 className="font-serif-display text-3xl md:text-4xl text-center mb-4" data-id="element-226">
              Qué incluye exactamente
            </h2>
            <p className="text-center text-white/50 text-lg mb-14" data-id="element-227">
              Al entrar, recibes:
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-228">
            <StaggerItem index={0} data-id="element-229">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-7 flex gap-5 items-start" data-id="element-230">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B5A]/15 flex items-center justify-center flex-shrink-0" data-id="element-231">
                  <BookOpenIcon className="w-6 h-6 text-[#FF6B5A]" data-id="element-232" />
                </div>
                <div data-id="element-233">
                  <h4 className="font-semibold text-lg mb-1" data-id="element-234">
                    Ebook completo (PDF)
                  </h4>
                  <p className="text-white/50" data-id="element-235">
                    Con las 3 fases y el plan día a día.
                  </p>
                </div>
              </div>
            </StaggerItem>

            <StaggerItem index={1} data-id="element-236">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-7 flex gap-5 items-start" data-id="element-237">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B5A]/15 flex items-center justify-center flex-shrink-0" data-id="element-238">
                  <CalendarIcon className="w-6 h-6 text-[#FF6B5A]" data-id="element-239" />
                </div>
                <div data-id="element-240">
                  <h4 className="font-semibold text-lg mb-1" data-id="element-241">
                    Calendario imprimible 21 días
                  </h4>
                  <p className="text-white/50" data-id="element-242">Para marcar y no pensar.</p>
                </div>
              </div>
            </StaggerItem>

            <StaggerItem index={2} data-id="element-243">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-7 flex gap-5 items-start" data-id="element-244">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B5A]/15 flex items-center justify-center flex-shrink-0" data-id="element-245">
                  <ListChecksIcon className="w-6 h-6 text-[#FF6B5A]" data-id="element-246" />
                </div>
                <div data-id="element-247">
                  <h4 className="font-semibold text-lg mb-1" data-id="element-248">
                    Tracker diario de 1 minuto
                  </h4>
                  <p className="text-white/50" data-id="element-249">
                    Energía, sueño, agua, cumplimiento.
                  </p>
                </div>
              </div>
            </StaggerItem>

            <StaggerItem index={3} data-id="element-250">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-7 flex gap-5 items-start" data-id="element-251">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B5A]/15 flex items-center justify-center flex-shrink-0" data-id="element-252">
                  <ActivityIcon className="w-6 h-6 text-[#FF6B5A]" data-id="element-253" />
                </div>
                <div data-id="element-254">
                  <h4 className="font-semibold text-lg mb-1" data-id="element-255">
                    Rutinas de emergencia 7–10 min
                  </h4>
                  <p className="text-white/50" data-id="element-256">Para días caóticos.</p>
                </div>
              </div>
            </StaggerItem>

            <StaggerItem index={4} data-id="element-257">
              <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-3xl p-7 flex gap-5 items-start" data-id="element-258">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B5A]/15 flex items-center justify-center flex-shrink-0" data-id="element-259">
                  <UtensilsIcon className="w-6 h-6 text-[#FF6B5A]" data-id="element-260" />
                </div>
                <div data-id="element-261">
                  <h4 className="font-semibold text-lg mb-1" data-id="element-262">
                    Guía de apoyo: "comida sin dieta"
                  </h4>
                  <p className="text-white/50" data-id="element-263">
                    Reglas simples para acompañar el reto.
                  </p>
                </div>
              </div>
            </StaggerItem>
          </div>

          <FadeInSection delay={0.3} data-id="element-264">
            <p className="text-center text-white/40 mt-10 text-lg" data-id="element-265">
              Todo descargable al instante.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 9) BENEFICIOS ─── */}
      <section className="bg-[#FAF7F4] text-[#1A1A1A]" data-id="element-266">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28" data-id="element-267">
          <FadeInSection data-id="element-268">
            <h2 className="font-serif-display text-3xl md:text-4xl mb-4" data-id="element-269">
              Esto es lo que normalmente pasa
            </h2>
            <p className="text-lg md:text-xl text-[#1A1A1A]/60 mb-12" data-id="element-270">
              cuando por fin tienes un sistema sencillo:
            </p>
          </FadeInSection>

          <div className="space-y-6" data-id="element-271">
            <StaggerItem index={0} data-id="element-272">
              <div className="flex items-start gap-5" data-id="element-273">
                <div className="w-10 h-10 rounded-full bg-[#FF6B5A]/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-274">
                  <SparklesIcon className="w-5 h-5 text-[#FF6B5A]" data-id="element-275" />
                </div>
                <p className="text-lg md:text-xl text-[#1A1A1A]/70 leading-relaxed" data-id="element-276">
                  Te vistes y{' '}
                  <strong className="text-[#1A1A1A]" data-id="element-277">te notas más firme</strong>{' '}
                  (aunque el peso no te obsesione).
                </p>
              </div>
            </StaggerItem>

            <StaggerItem index={1} data-id="element-278">
              <div className="flex items-start gap-5" data-id="element-279">
                <div className="w-10 h-10 rounded-full bg-[#FF6B5A]/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-280">
                  <ZapIcon className="w-5 h-5 text-[#FF6B5A]" data-id="element-281" />
                </div>
                <p className="text-lg md:text-xl text-[#1A1A1A]/70 leading-relaxed" data-id="element-282">
                  <strong className="text-[#1A1A1A]" data-id="element-283">Recuperas energía</strong>{' '}
                  porque tu cuerpo "despierta".
                </p>
              </div>
            </StaggerItem>

            <StaggerItem index={2} data-id="element-284">
              <div className="flex items-start gap-5" data-id="element-285">
                <div className="w-10 h-10 rounded-full bg-[#FF6B5A]/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-286">
                  <HeartIcon className="w-5 h-5 text-[#FF6B5A]" data-id="element-287" />
                </div>
                <p className="text-lg md:text-xl text-[#1A1A1A]/70 leading-relaxed" data-id="element-288">
                  Te sientes{' '}
                  <strong className="text-[#1A1A1A]" data-id="element-289">
                    más ligera al moverte.
                  </strong>
                </p>
              </div>
            </StaggerItem>

            <StaggerItem index={3} data-id="element-290">
              <div className="flex items-start gap-5" data-id="element-291">
                <div className="w-10 h-10 rounded-full bg-[#FF6B5A]/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-292">
                  <FlameIcon className="w-5 h-5 text-[#FF6B5A]" data-id="element-293" />
                </div>
                <div data-id="element-294">
                  <p className="text-lg md:text-xl text-[#1A1A1A]/70 leading-relaxed" data-id="element-295">
                    Dejas de empezar y dejarlo: creas una identidad nueva —
                  </p>
                  <p className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] mt-2 italic" data-id="element-296">
                    "soy una persona que se cumple"
                  </p>
                </div>
              </div>
            </StaggerItem>

            <StaggerItem index={4} data-id="element-297">
              <div className="flex items-start gap-5" data-id="element-298">
                <div className="w-10 h-10 rounded-full bg-[#FF6B5A]/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-299">
                  <ShieldCheckIcon className="w-5 h-5 text-[#FF6B5A]" data-id="element-300" />
                </div>
                <div data-id="element-301">
                  <p className="text-lg md:text-xl text-[#1A1A1A]/70 leading-relaxed" data-id="element-302">
                    Y lo más importante:{' '}
                    <strong className="text-[#1A1A1A]" data-id="element-303">
                      vuelve la confianza
                    </strong>
                  </p>
                  <p className="text-lg text-[#1A1A1A]/50 mt-1" data-id="element-304">
                    porque ya no dependes de motivación.
                  </p>
                </div>
              </div>
            </StaggerItem>
          </div>

          <FadeInSection delay={0.2} data-id="element-305">
            <motion.img src="https://cdn.magicpatterns.com/uploads/uZYiY4RJnijHKQ7mJbJLPS/heylloyd-image-1771196764845.png" alt="Mujer mirándose al espejo con confianza después de entrenar" className="w-full rounded-3xl shadow-lg object-cover mt-12" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-306" />
          </FadeInSection>
        </div>
      </section>

      {/* ─── 10) CÓMO SE VE UN DÍA ─── */}
      <section className="bg-[#1A1A1A] text-white" data-id="element-307">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28" data-id="element-308">
          <FadeInSection data-id="element-309">
            <p className="text-[#FF6B5A] font-semibold text-sm tracking-widest uppercase mb-4 text-center" data-id="element-310">
              Prueba de simplicidad
            </p>
            <h2 className="font-serif-display text-3xl md:text-4xl text-center mb-14" data-id="element-311">
              Cómo se ve un día
            </h2>
          </FadeInSection>

          <FadeInSection delay={0.1} data-id="element-312">
            <p className="text-lg text-white/60 text-center mb-10" data-id="element-313">
              Cada sesión sigue esta estructura:
            </p>
          </FadeInSection>

          <div className="max-w-md mx-auto space-y-0" data-id="element-314">
            <StaggerItem index={0} data-id="element-315">
              <div className="relative pl-10 pb-10 border-l-2 border-[#FF6B5A]/30" data-id="element-316">
                <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-[#FF6B5A]" data-id="element-317" />
                <p className="text-sm text-[#FF6B5A] font-semibold mb-1" data-id="element-318">
                  3–4 min
                </p>
                <p className="text-xl text-white/80" data-id="element-319">Calentamiento simple</p>
              </div>
            </StaggerItem>
            <StaggerItem index={1} data-id="element-320">
              <div className="relative pl-10 pb-10 border-l-2 border-[#FF6B5A]/30" data-id="element-321">
                <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-[#FF6B5A]" data-id="element-322" />
                <p className="text-sm text-[#FF6B5A] font-semibold mb-1" data-id="element-323">
                  14–15 min
                </p>
                <p className="text-xl text-white/80" data-id="element-324">Circuito guiado</p>
              </div>
            </StaggerItem>
            <StaggerItem index={2} data-id="element-325">
              <div className="relative pl-10" data-id="element-326">
                <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-[#FF6B5A]" data-id="element-327" />
                <p className="text-sm text-[#FF6B5A] font-semibold mb-1" data-id="element-328">
                  1–2 min
                </p>
                <p className="text-xl text-white/80" data-id="element-329">Vuelta a la calma</p>
              </div>
            </StaggerItem>
          </div>

          <FadeInSection delay={0.3} data-id="element-330">
            <div className="text-center mt-14" data-id="element-331">
              <p className="font-serif-display text-3xl md:text-4xl text-white mb-4" data-id="element-332">
                Total: 20 min.
              </p>
              <p className="text-lg text-white/50" data-id="element-333">
                Sin apps. Sin vídeos eternos. Sin castigos.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 11) PARA QUIÉN ES / NO ES ─── */}
      <section className="bg-[#FAF7F4] text-[#1A1A1A]" data-id="element-334">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28" data-id="element-335">
          <FadeInSection data-id="element-336">
            <h2 className="font-serif-display text-3xl md:text-4xl text-center mb-14" data-id="element-337">
              ¿Es para ti?
            </h2>
          </FadeInSection>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-338">
            <FadeInSection delay={0.1} data-id="element-339">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#1A1A1A]/5" data-id="element-340">
                <h3 className="font-serif-display text-2xl text-emerald-700 mb-6" data-id="element-341">
                  Es para ti si…
                </h3>
                <ul className="space-y-4" data-id="element-342">
                  <li className="flex items-start gap-3" data-id="element-343">
                    <CheckCircleIcon className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-344" />
                    <span className="text-lg text-[#1A1A1A]/70" data-id="element-345">
                      Eres principiante o estás retomando
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-346">
                    <CheckCircleIcon className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-347" />
                    <span className="text-lg text-[#1A1A1A]/70" data-id="element-348">
                      Quieres entrenar en casa sin complicarte
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-349">
                    <CheckCircleIcon className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-350" />
                    <span className="text-lg text-[#1A1A1A]/70" data-id="element-351">
                      Te cuesta ser constante y necesitas estructura
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-352">
                    <CheckCircleIcon className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-353" />
                    <span className="text-lg text-[#1A1A1A]/70" data-id="element-354">
                      Te motiva un plan corto y claro
                    </span>
                  </li>
                </ul>
              </div>
            </FadeInSection>

            <FadeInSection delay={0.2} data-id="element-355">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#1A1A1A]/5" data-id="element-356">
                <h3 className="font-serif-display text-2xl text-[#1A1A1A]/40 mb-6" data-id="element-357">
                  No es para ti si…
                </h3>
                <ul className="space-y-4" data-id="element-358">
                  <li className="flex items-start gap-3" data-id="element-359">
                    <XCircleIcon className="w-6 h-6 text-[#1A1A1A]/30 flex-shrink-0 mt-0.5" data-id="element-360" />
                    <span className="text-lg text-[#1A1A1A]/50" data-id="element-361">
                      Buscas un plan avanzado tipo gimnasio/hipertrofia
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-362">
                    <XCircleIcon className="w-6 h-6 text-[#1A1A1A]/30 flex-shrink-0 mt-0.5" data-id="element-363" />
                    <span className="text-lg text-[#1A1A1A]/50" data-id="element-364">
                      Necesitas entrenamientos largos diarios
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-365">
                    <XCircleIcon className="w-6 h-6 text-[#1A1A1A]/30 flex-shrink-0 mt-0.5" data-id="element-366" />
                    <span className="text-lg text-[#1A1A1A]/50" data-id="element-367">
                      Tienes una lesión activa y no puedes adaptar ejercicios
                      (en ese caso: profesional)
                    </span>
                  </li>
                </ul>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ─── 12) OBJECIONES ─── */}
      <section className="bg-[#1A1A1A] text-white" data-id="element-368">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28" data-id="element-369">
          <FadeInSection data-id="element-370">
            <h2 className="font-serif-display text-3xl md:text-4xl text-center mb-14" data-id="element-371">
              Antes de que lo pienses…
            </h2>
          </FadeInSection>

          <div className="space-y-8" data-id="element-372">
            <StaggerItem index={0} data-id="element-373">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-8" data-id="element-374">
                <p className="font-serif-display text-xl text-[#FF6B5A] mb-3 italic" data-id="element-375">
                  "¿Y si no tengo nada de material?"
                </p>
                <p className="text-lg text-white/60 leading-relaxed" data-id="element-376">
                  Perfecto. Se puede hacer con silla, pared y tu propio peso (y
                  una mochila opcional).
                </p>
              </div>
            </StaggerItem>

            <StaggerItem index={1} data-id="element-377">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-8" data-id="element-378">
                <p className="font-serif-display text-xl text-[#FF6B5A] mb-3 italic" data-id="element-379">
                  "¿Y si me salto un día?"
                </p>
                <p className="text-lg text-white/60 leading-relaxed" data-id="element-380">
                  No reinicias. Continúas. El objetivo es continuidad, no
                  perfección.
                </p>
              </div>
            </StaggerItem>

            <StaggerItem index={2} data-id="element-381">
              <div className="bg-white/5 border border-white/10 rounded-3xl p-8" data-id="element-382">
                <p className="font-serif-display text-xl text-[#FF6B5A] mb-3 italic" data-id="element-383">
                  "¿Y si no tengo ganas?"
                </p>
                <p className="text-lg text-white/60 leading-relaxed" data-id="element-384">
                  Por eso existen las rutinas de emergencia de 7–10 minutos:
                  para no romper el hábito.
                </p>
              </div>
            </StaggerItem>
          </div>
        </div>
      </section>

      {/* ─── 13) GARANTÍA ─── */}
      <section className="bg-[#FAF7F4] text-[#1A1A1A]" data-id="element-385">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28 text-center" data-id="element-386">
          <FadeInSection data-id="element-387">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FF6B5A]/10 mb-8" data-id="element-388">
              <ShieldCheckIcon className="w-10 h-10 text-[#FF6B5A]" data-id="element-389" />
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl mb-6" data-id="element-390">
              Pruébalo sin presión
            </h2>
            <p className="text-lg md:text-xl text-[#1A1A1A]/60 leading-relaxed max-w-lg mx-auto mb-4" data-id="element-391">
              Tienes poco que perder y mucho que ganar.
            </p>
            <p className="text-lg md:text-xl text-[#1A1A1A]/60 font-semibold" data-id="element-392">
              Sin dramas.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 14) URGENCIA / DECISIÓN ─── */}
      <section className="bg-[#1A1A1A] text-white" data-id="element-393">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28 text-center" data-id="element-394">
          <FadeInSection data-id="element-395">
            <p className="text-lg md:text-xl text-white/50 leading-relaxed mb-6" data-id="element-396">
              Puedes seguir guardando rutinas en Instagram…
            </p>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-10" data-id="element-397">
              o puedes tener un plan que te diga exactamente qué hacer hoy, en
              20 minutos, y punto.
            </p>
            <p className="font-serif-display text-2xl md:text-3xl text-white mb-3" data-id="element-398">
              Porque cuidarte no debería ser un proyecto infinito.
            </p>
            <p className="font-serif-display text-2xl md:text-3xl text-[#FF6B5A]" data-id="element-399">
              Debería ser algo que encaje en tu vida.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 15) CTA FINAL ─── */}
      <section id="comprar" className="bg-gradient-to-br from-[#FF6B5A] to-[#E85A4A] text-white" data-id="element-400">
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28 text-center" data-id="element-401">
          <FadeInSection data-id="element-402">
            <h2 className="font-serif-display text-3xl md:text-5xl mb-6 leading-tight" data-id="element-403">
              Empieza hoy con el Reto 21 días: Cuerpo en Casa (Principiantes)
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-4" data-id="element-404">
              Descarga inmediata (PDF + calendario + tracker + rutinas de
              emergencia).
            </p>
          </FadeInSection>

          <FadeInSection delay={0.15} data-id="element-405">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-406">
              <span className="text-white/50 text-lg line-through" data-id="element-407">
                19,99 €
              </span>
              <span className="font-serif-display text-4xl md:text-5xl text-white font-bold" data-id="element-408">
                6,36 €
              </span>
              <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-409">
                68% OFF
              </span>
            </div>
            <div className="mb-10" data-id="element-410">
              <motion.a href="https://www.warame.com/cart/56929413136766:1" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} whileHover={{
              scale: 1.04
            }} whileTap={{
              scale: 0.97
            }} className="inline-flex items-center justify-center gap-3 bg-white text-[#1A1A1A] font-bold rounded-full px-10 py-5 text-lg md:text-xl shadow-xl hover:shadow-2xl transition-shadow" data-id="element-411">
                Quiero empezar hoy el Sistema 21×20
                <ArrowRightIcon className="w-5 h-5" data-id="element-412" />
              </motion.a>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.25} data-id="element-413">
            <p className="text-white/70 text-lg max-w-md mx-auto leading-relaxed" data-id="element-414">
              Recuerda: no necesitas motivación perfecta.{' '}
              <strong className="text-white" data-id="element-415">
                Necesitas un sistema que funcione incluso cuando la motivación
                falla.
              </strong>
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ─── 16) P.S. ─── */}
      <section className="bg-[#1A1A1A] text-white" data-id="element-416">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20" data-id="element-417">
          <FadeInSection data-id="element-418">
            <p className="text-lg md:text-xl text-white/50 leading-relaxed" data-id="element-419">
              <strong className="text-white/70" data-id="element-420">P.D.:</strong> Si ahora mismo
              estás en ese punto de "quiero cambiar pero no sé por dónde
              empezar", este reto está hecho para eso: darte un camino de 21
              días tan simple que lo único que tengas que hacer…{' '}
              <strong className="text-[#FF6B5A]" data-id="element-421">es seguirlo.</strong>
            </p>
          </FadeInSection>

          <SectionDivider dark data-id="element-422" />

          <div className="text-center mt-8" data-id="element-423">
            <CTAButton data-id="element-424" />
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-[#111111] text-white/40 border-t border-white/5" data-id="element-425">
        <div className="max-w-3xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm" data-id="element-426">
          <span data-id="element-427">© 2026 - Reto 21 días</span>
          <span className="hidden sm:inline text-white/20" data-id="element-428">·</span>
          <button onClick={() => setPrivacyOpen(true)} className="hover:text-white/70 transition-colors underline underline-offset-2" data-id="element-429">
            Política de Privacidad
          </button>
          <span className="hidden sm:inline text-white/20" data-id="element-430">·</span>
          <button onClick={() => setTermsOpen(true)} className="hover:text-white/70 transition-colors underline underline-offset-2" data-id="element-431">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* ─── Legal Modals ─── */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-432">
        <p data-id="element-433">
          <strong className="text-[#1A1A1A]" data-id="element-434">
            1. Responsable del tratamiento
          </strong>
        </p>
        <p data-id="element-435">
          El responsable del tratamiento de tus datos personales es el titular
          de este sitio web. Al adquirir el producto o suscribirte, aceptas el
          tratamiento de tus datos conforme a esta política.
        </p>

        <p data-id="element-436">
          <strong className="text-[#1A1A1A]" data-id="element-437">2. Datos que recopilamos</strong>
        </p>
        <p data-id="element-438">
          Recopilamos únicamente los datos necesarios para procesar tu compra:
          nombre, dirección de correo electrónico y datos de pago. No
          almacenamos datos de tarjetas de crédito directamente; estos son
          gestionados por nuestro procesador de pagos.
        </p>

        <p data-id="element-439">
          <strong className="text-[#1A1A1A]" data-id="element-440">
            3. Finalidad del tratamiento
          </strong>
        </p>
        <p data-id="element-441">
          Tus datos se utilizan para: procesar y entregar tu compra, enviarte el
          producto digital adquirido, comunicaciones relacionadas con tu pedido
          y, si lo autorizas, enviarte contenido informativo o promocional.
        </p>

        <p data-id="element-442">
          <strong className="text-[#1A1A1A]" data-id="element-443">4. Base legal</strong>
        </p>
        <p data-id="element-444">
          El tratamiento de tus datos se basa en la ejecución del contrato de
          compra y, en su caso, en tu consentimiento expreso para comunicaciones
          comerciales.
        </p>

        <p data-id="element-445">
          <strong className="text-[#1A1A1A]" data-id="element-446">5. Conservación de datos</strong>
        </p>
        <p data-id="element-447">
          Tus datos se conservarán mientras sea necesario para cumplir con la
          finalidad para la que fueron recogidos y para cumplir con las
          obligaciones legales aplicables.
        </p>

        <p data-id="element-448">
          <strong className="text-[#1A1A1A]" data-id="element-449">6. Derechos del usuario</strong>
        </p>
        <p data-id="element-450">
          Puedes ejercer tus derechos de acceso, rectificación, supresión,
          limitación, portabilidad y oposición en cualquier momento.
        </p>

        <p data-id="element-451">
          <strong className="text-[#1A1A1A]" data-id="element-452">7. Cookies</strong>
        </p>
        <p data-id="element-453">
          Este sitio puede utilizar cookies técnicas y analíticas para mejorar
          la experiencia de navegación. Puedes configurar tu navegador para
          rechazarlas.
        </p>
      </LegalModal>

      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-454">
        <p data-id="element-455">
          <strong className="text-[#1A1A1A]" data-id="element-456">1. Objeto</strong>
        </p>
        <p data-id="element-457">
          Estos términos regulan la compra y uso del producto digital "Reto 21
          días: Cuerpo en Casa (Principiantes)" — un ebook en formato PDF con
          material complementario.
        </p>

        <p data-id="element-458">
          <strong className="text-[#1A1A1A]" data-id="element-459">2. Producto digital</strong>
        </p>
        <p data-id="element-460">
          Al completar la compra, recibirás acceso inmediato al ebook y
          materiales complementarios en formato digital (PDF). Se trata de un
          producto de descarga inmediata.
        </p>

        <p data-id="element-461">
          <strong className="text-[#1A1A1A]" data-id="element-462">3. Precio y pago</strong>
        </p>
        <p data-id="element-463">
          El precio del producto es el indicado en la página de venta en el
          momento de la compra. Todos los precios incluyen los impuestos
          aplicables. El pago se realiza a través de plataformas de pago
          seguras.
        </p>

        <p data-id="element-464">
          <strong className="text-[#1A1A1A]" data-id="element-465">
            4. Garantía de satisfacción
          </strong>
        </p>
        <p data-id="element-466">
          Ofrecemos una garantía de 7 días desde la fecha de compra. Si no estás
          satisfecho/a, puedes solicitar el reembolso completo dentro de ese
          plazo sin necesidad de justificación.
        </p>

        <p data-id="element-467">
          <strong className="text-[#1A1A1A]" data-id="element-468">5. Propiedad intelectual</strong>
        </p>
        <p data-id="element-469">
          Todo el contenido del producto (textos, diseños, rutinas, metodología)
          está protegido por derechos de propiedad intelectual. La compra otorga
          una licencia de uso personal e intransferible. Queda prohibida su
          reproducción, distribución o reventa.
        </p>

        <p data-id="element-470">
          <strong className="text-[#1A1A1A]" data-id="element-471">
            6. Limitación de responsabilidad
          </strong>
        </p>
        <p data-id="element-472">
          Este producto tiene fines informativos y educativos. No sustituye el
          consejo médico profesional. Consulta con un profesional de la salud
          antes de comenzar cualquier programa de ejercicio. El uso del producto
          es bajo tu propia responsabilidad.
        </p>

        <p data-id="element-473">
          <strong className="text-[#1A1A1A]" data-id="element-474">7. Modificaciones</strong>
        </p>
        <p data-id="element-475">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones no afectarán a las compras ya realizadas.
        </p>
      </LegalModal>
    </main>;
}